import { State, Selector, Action, StateContext, createSelector } from '@ngxs/store';
import {
  LoadUsersAction,
  LoadUsersSuccessAction,
  LoadUsersFailAction,
  UpdateUserAction,
  UpdateUserSuccessAction,
  UpdateUserFailAction,
  LoadUserAction,
  LoadUserSuccessAction,
  LoadUserFailAction,
  ToggleIsActiveUserAction,
  ToggleIsActiveUserSuccessAction,
  ToggleIsActiveUserFailAction,
  RegisterUserAction,
  RegisterUserSuccessAction,
  RegisterUserFailAction,
  LoadAdUsersAction,
  LoadAdUsersSuccessAction,
  LoadAdUsersFailAction,
  RegisterSignalRHandlersForUsersAction,
  ToggleUserConnectionStatusAction,
} from './users.actions';
import { tap, catchError } from 'rxjs/operators';
import { Navigate } from '@ngxs/router-plugin';
import { OpenSnackbarAction } from '@ngxs-store/ui-store/ui.actions';
import { SetFormPristine } from '@ngxs/form-plugin';
import { RolesState } from '@ngxs-store/roles-store/roles.state';
import { Injectable } from '@angular/core';
import { UserModel, ActiveDirectoryUserModel, UsersClient, RoleModel, TenantModel } from '@core/services/api.service';
import { SignalRService } from '@core/services/signalr.service';
import { SnackbarType } from '@core/models';
import { TenantsState } from '@ngxs-store/tenants-store/tenants.state';

export class UsersStateModel {
  public isLoading: boolean;
  public signalRHandlersRegistered: boolean;
  public users: UserModel[];
  public adUsers: ActiveDirectoryUserModel[];
}

@State<UsersStateModel>({
  name: 'users',
  defaults: {
    isLoading: false,
    signalRHandlersRegistered: false,
    users: [],
    adUsers: []
  }
})
@Injectable()
export class UsersState {
  constructor(
    private usersClient: UsersClient,
    public signalRService: SignalRService
  ) { }

  @Selector()
  static getIsLoading(state: UsersStateModel) {
    return state.isLoading;
  }

  @Selector()
  static getUsers(state: UsersStateModel) {
    return state.users;
  }

  @Selector()
  static getAdUsers(state: UsersStateModel) {
    return state.adUsers;
  }

  static getUser(userId: string) {
    return createSelector([UsersState], (usersState: UsersStateModel) => {
      return usersState.users.find((u) => u.id === userId);
    });
  }

  static getUserWithToggledRoles(userId: string) {
    return createSelector([UsersState.getUser(userId), RolesState.getRoles], (user: UserModel, roles: RoleModel[]) => {
      if (user && roles) {
        return new UserModel({
          ...user,
          roles: [...roles.map((role) => {
            return new RoleModel({
              id: role.id, name: role.name, description: role.description, enabled: user.roles.some((r) => r.id === role.id) ? true : false
            });
          })]
        });
      }
    });
  }

  static getUserWithToggledRolesAndTenants(userId: string) {
    return createSelector([UsersState.getUser(userId), RolesState.getRoles, TenantsState.getTenants], (user: UserModel, roles: RoleModel[], tenants: TenantModel[]) => {
      if (user && roles && tenants) {
        return new UserModel({
          ...user,
          roles: [...roles.map((role) => {
            return new RoleModel({
              id: role.id, name: role.name, description: role.description, enabled: user.roles.some((r) => r.id === role.id) ? true : false
            });
          })],
          tenants: [...tenants.map((tenant) => {
            return new TenantModel({
              id: tenant.id, name: tenant.name, enabled: user.tenants.some((t) => t.id === tenant.id) ? true : false
            });
          })]
        });
      }
    });
  }

  @Action(LoadUserAction)
  loadUserAction({ dispatch, patchState }: StateContext<UsersStateModel>, action: LoadUserAction) {
    patchState({ isLoading: true });

    return this.usersClient.getUserById(action.payload.userId).pipe(
      tap((user) => dispatch(new LoadUserSuccessAction({ user }))),
      catchError((err) => dispatch(new LoadUserFailAction()))
    );
  }

  @Action(LoadUserSuccessAction)
  loadUserSuccessAction(ctx: StateContext<UsersStateModel>, action: LoadUserSuccessAction) {
    const state = ctx.getState();

    ctx.patchState({
      isLoading: false,
      users:
        state.users.findIndex((v) => v.id === action.payload.user.id) > -1
          ? state.users.map((user) => (user.id === action.payload.user.id ? action.payload.user : user))
          : [...state.users, action.payload.user]
    });
  }

  @Action(LoadUserFailAction)
  loadUserFailAction(ctx: StateContext<UsersStateModel>, action: LoadUserFailAction) {
    ctx.patchState({
      isLoading: false
    });
  }

  @Action(LoadUsersAction)
  loadUsersAction({ dispatch, patchState }: StateContext<UsersStateModel>, action: LoadUsersAction) {
    patchState({ isLoading: true });

    return this.usersClient.getAllUsers().pipe(
      tap((users) => dispatch(new LoadUsersSuccessAction({ users }))),
      catchError((err) => dispatch(new LoadUsersFailAction()))
    );
  }

  @Action(LoadUsersSuccessAction)
  loadUsersSuccessAction(ctx: StateContext<UsersStateModel>, action: LoadUsersSuccessAction) {
    ctx.patchState({
      isLoading: false,
      users: action.payload.users
    });
  }

  @Action(LoadUsersFailAction)
  loadUsersFailAction(ctx: StateContext<UsersStateModel>, action: LoadUsersFailAction) {
    ctx.patchState({
      isLoading: false,
      users: []
    });
  }

  @Action(UpdateUserAction)
  updateUserAction({ dispatch, patchState }: StateContext<UsersStateModel>, action: UpdateUserAction) {
    patchState({ isLoading: true });

    return this.usersClient.updateUser(action.payload.updateUserCommand).pipe(
      tap((user) => dispatch(new UpdateUserSuccessAction({ user }))),
      catchError((err) => dispatch(new UpdateUserFailAction()))
    );
  }

  @Action(UpdateUserSuccessAction)
  updateUserSuccessAction({ dispatch, getState, patchState }: StateContext<UsersStateModel>, action: UpdateUserSuccessAction) {
    const state = getState();

    patchState({
      isLoading: false,
      users: state.users.map((user) => (user.id === action.payload.user.id ? action.payload.user : user))
    });

    dispatch(new SetFormPristine('forms.editUserForm'));
    dispatch(new OpenSnackbarAction({ message: 'User updated successfully.', type: SnackbarType.Simple }));
  }

  @Action(UpdateUserFailAction)
  updateUserFailAction({ dispatch, getState, patchState }: StateContext<UsersStateModel>, action: UpdateUserFailAction) {
    patchState({
      isLoading: false
    });
  }

  @Action(ToggleIsActiveUserAction)
  toggleIsActiveUserAction({ dispatch, patchState }: StateContext<UsersStateModel>, action: ToggleIsActiveUserAction) {
    patchState({ isLoading: true });

    return this.usersClient[action.payload.user.isActive ? 'disableUser' : 'enableUser'](action.payload.user.id).pipe(
      tap(() => dispatch(new ToggleIsActiveUserSuccessAction({ user: action.payload.user }))),
      catchError((err) => dispatch(new ToggleIsActiveUserFailAction({ user: action.payload.user })))
    );
  }

  @Action(ToggleIsActiveUserSuccessAction)
  toggleIsActiveUserSuccessAction({ dispatch, getState, patchState }: StateContext<UsersStateModel>, action: ToggleIsActiveUserSuccessAction) {
    const state = getState();

    patchState({
      isLoading: false,
      users: state.users.map((user) => (user.id === action.payload.user.id ? new UserModel({ ...action.payload.user, isActive: !action.payload.user.isActive }) : user))
    });

    dispatch(new OpenSnackbarAction({ message: `User ${action.payload.user.isActive ? 'disabled' : 'enabled'}.`, type: SnackbarType.Simple }));
  }

  @Action(ToggleIsActiveUserFailAction)
  toggleIsActiveUserFailAction({ getState, patchState }: StateContext<UsersStateModel>, action: ToggleIsActiveUserFailAction) {
    const state = getState();

    patchState({
      isLoading: false,
      users: state.users.map((user) => (user.id === action.payload.user.id ? new UserModel({ ...action.payload.user }) : user))
    });
  }

  @Action(RegisterUserAction)
  registerUserAction({ dispatch, patchState }: StateContext<UsersStateModel>, action: RegisterUserAction) {
    patchState({ isLoading: true });

    return this.usersClient.createUser(action.payload.createUserCommand).pipe(
      tap((user) => dispatch(new RegisterUserSuccessAction({ user }))),
      catchError((err) => dispatch(new RegisterUserFailAction()))
    );
  }

  @Action(RegisterUserSuccessAction)
  registerUserSuccessAction({ dispatch, getState, patchState }: StateContext<UsersStateModel>, action: RegisterUserSuccessAction) {
    const state = getState();

    patchState({
      isLoading: false,
      users: [...state.users, action.payload.user]
    });

    dispatch(new Navigate(['/users']));
    dispatch(new OpenSnackbarAction({ message: 'User created successfully.', type: SnackbarType.Simple }));
  }

  @Action(RegisterUserFailAction)
  registerUserFailAction({ dispatch, getState, patchState }: StateContext<UsersStateModel>, action: RegisterUserFailAction) {
    patchState({
      isLoading: false
    });
  }

  @Action(LoadAdUsersAction)
  loadAdUsersAction({ dispatch, patchState }: StateContext<UsersStateModel>, action: LoadAdUsersAction) {
    patchState({ isLoading: true });

    return this.usersClient.searchActiveDirectoryUsers(action.payload.searchTerm).pipe(
      tap((adUsers) => dispatch(new LoadAdUsersSuccessAction({ adUsers }))),
      catchError((err) => dispatch(new LoadAdUsersFailAction()))
    );
  }

  @Action(LoadAdUsersSuccessAction)
  loadAdUsersSuccessAction(ctx: StateContext<UsersStateModel>, action: LoadAdUsersSuccessAction) {
    ctx.patchState({
      isLoading: false,
      adUsers: action.payload.adUsers
    });
  }

  @Action(LoadAdUsersFailAction)
  loadAdUsersFailAction(ctx: StateContext<UsersStateModel>) {
    ctx.patchState({
      isLoading: false,
      adUsers: []
    });
  }

  @Action(RegisterSignalRHandlersForUsersAction)
  registerSignalRHandlersForUsersAction({ getState, dispatch, patchState }: StateContext<UsersStateModel>) {
    const state = getState();

    if (!state.signalRHandlersRegistered) {

      this.signalRService.getHubConnection().on('Connected', (username: string) => {
        dispatch(new ToggleUserConnectionStatusAction({ username, connected: true }));
      });

      this.signalRService.getHubConnection().on('Disconnected', (username: string) => {
        dispatch(new ToggleUserConnectionStatusAction({ username, connected: false }));
      });

      patchState({
        signalRHandlersRegistered: true
      });
    }
  }

  @Action(ToggleUserConnectionStatusAction)
  toggleUserConnectionStatusAction({ getState, patchState }: StateContext<UsersStateModel>, action: ToggleUserConnectionStatusAction) {
    const state = getState();

    patchState({
      users: state.users.map((user) => (user.username === action.payload.username ? new UserModel({ ...user, signalRConnected: action.payload.connected }) : user))
    });
  }
}
